import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { siteClock } from "./siteClock";

// Phase 5.8 (Feb 2026) — Sticky selected date across page navigation.
//
// Ops complained that picking a date on Schedule then navigating to MyDay,
// Availability, Corporate, or Coaches reset the picker to today. This
// provider is the shared source of truth for the "selected date" concept
// across those 5 date-driven pages. Backed by `localStorage.pec_selectedDate`
// so it survives full reloads / new-tab openings on the same browser.
//
// Design invariants (per user-approved Phase 5.8 spec):
//   • URL `?date=YYYY-MM-DD` wins on mount when present + valid; also
//     persists into localStorage so subsequent navigation inherits it.
//   • `setDate(iso)` updates context state + localStorage but does NOT
//     rewrite `window.location` — the URL stays sticky to whatever the user
//     landed on. Prevents history-bloat + back-button friction.
//   • Provider does NOT wire the `storage` event — tabs diverge by design
//     (users open tabs intentionally for different days).
//   • `useSelectedDate()` outside a provider falls back to a local
//     `useState(todayISO())` so the hook can never crash a page —
//     invisible rollback path.
//   • localStorage failures (SecurityError in private mode, iframe sandbox)
//     downgrade to in-memory state and warn once. Feature keeps working
//     within-tab; sticky-across-reload is lost.

// Storage key namespaced with `pec_` so unrelated tools don't clash with a
// bare "selectedDate" key.
const STORAGE_KEY = "pec_selectedDate";

// Operations dates follow PEC Atlanta, including operators in other timezones.
export const todayISO = () => siteClock().date;

// Reuses the same validation shape as `scheduleDateFromQuery.js` (Phase 15
// deep-link helper). Rejects malformed strings, out-of-range dates, and
// anything that fails Date.parse.
const isValidISODate = (v) => {
  if (typeof v !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const parsed = Date.parse(v);
  return !Number.isNaN(parsed) && new Date(parsed).toISOString().slice(0, 10) === v;
};

const dateFromSearch = (search) => {
  const date = new URLSearchParams(search).get("date");
  return isValidISODate(date) ? date : null;
};

const readURLDate = () => {
  if (typeof window === "undefined") return null;
  try {
    return dateFromSearch(window.location.search);
  } catch {
    return null;
  }
};

const safeGetStorage = () => {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch (e) {
    if (!safeGetStorage._warned) {
      console.warn("[selectedDate] localStorage unavailable, sticky date disabled for this session:", e?.message);
      safeGetStorage._warned = true;
    }
    return null;
  }
};

const safeSetStorage = (v) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, v);
  } catch (e) {
    if (!safeSetStorage._warned) {
      console.warn("[selectedDate] localStorage write failed, sticky date disabled:", e?.message);
      safeSetStorage._warned = true;
    }
  }
};

// Resolve the initial date: URL param → localStorage → today. Runs once
// per provider mount inside `useState`'s init callback so the resolution
// cost is zero on every re-render.
const resolveInitialDate = () => {
  const fromUrl = readURLDate();
  if (fromUrl) return { value: fromUrl, source: "url" };
  const stored = safeGetStorage();
  if (isValidISODate(stored)) return { value: stored, source: "storage" };
  return { value: todayISO(), source: "default" };
};

const SelectedDateContext = createContext(null);

export function SelectedDateProvider({ children }) {
  const [date, setDateState] = useState(() => resolveInitialDate().value);

  // URL param wins on mount AND persists into localStorage so subsequent
  // navigation inherits it. Handled once per mount inside an effect (not
  // during render) to keep the useState init pure.
  useEffect(() => {
    const initial = resolveInitialDate();
    if (initial.source === "url") {
      safeSetStorage(initial.value);
    } else if (initial.source === "default") {
      // Prime storage with today so a subsequent read is a hit, keeps
      // localStorage in sync with what the provider is handing out.
      safeSetStorage(initial.value);
    }
    // `source === "storage"` — already persisted; nothing to do.
    // Intentionally runs only once — the URL is read once on mount per
    // Phase 5.8 spec ("provider only READS the URL on mount, never writes it").
  }, []);

  const setDate = useCallback((iso) => {
    if (!isValidISODate(iso)) {
      console.warn("[selectedDate] setDate ignored invalid ISO:", iso);
      return;
    }
    setDateState(iso);
    safeSetStorage(iso);
  }, []);

  const value = useMemo(() => [date, setDate], [date, setDate]);

  return (
    <SelectedDateContext.Provider value={value}>
      {children}
    </SelectedDateContext.Provider>
  );
}

// Consumer hook — behaves like `useState<string>` (returns `[date, setDate]`).
//
// If called outside a `<SelectedDateProvider>`, falls back to a local
// `useState(todayISO())`. This means:
//   • Unit tests can render a component in isolation without a provider
//   • The invisible rollback path (unmount provider) doesn't crash any page
//   • Storybook-style previews just work
export function useSelectedDate() {
  const ctx = useContext(SelectedDateContext);
  const [fallback, setFallback] = useState(todayISO);
  if (ctx) return ctx;
  return [fallback, setFallback];
}


// Feb 2026 — URL-date deep-link sync (fold-in after PB-2).
//
// Mount this component INSIDE <BrowserRouter> as a sibling of <Routes>.
// It listens for URL `?date=YYYY-MM-DD` changes on SPA navigation and
// forwards them into the SelectedDateProvider. Kept in a separate hook
// so the provider stays Router-agnostic (unit tests can mount the
// provider without a Router, per Phase 5.8 shape).
//
// A dated link selects its date on navigation. Changing the working date
// or replacing search/filter parameters must not reapply the original link.
// The provider never writes URLs; date-less navigation keeps the working date.
const DATE_ACTION_KEYS = ["session", "requirement", "view", "pair", "assign", "simulators", "import", "walkin", "print"];
export function URLDateSync() {
  const ctx = useContext(SelectedDateContext);
  const location = useLocation();
  const navigationType = useNavigationType();
  const previousLocation = useRef(null);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const next = {
      key: location.key,
      pathname: location.pathname,
      search: location.search,
      date: dateFromSearch(location.search),
      intent: Object.fromEntries(DATE_ACTION_KEYS.map(key => [key, params.get(key) || ""])),
    };
    const previous = previousLocation.current;
    // Record navigation before updating context so the resulting rerender
    // cannot undo an operator's date selection.
    previousLocation.current = next;
    if (!ctx || !next.date) return;
    const changedLocation = !previous || next.key !== previous.key || next.pathname !== previous.pathname || next.search !== previous.search;
    if (!changedLocation) return;
    const explicitIntent = previous && DATE_ACTION_KEYS.some(key => next.intent[key] && next.intent[key] !== previous.intent[key]);
    const selectFromLink = !previous || next.pathname !== previous.pathname || next.date !== previous.date ||
      (next.key !== previous.key && navigationType !== "REPLACE") || explicitIntent;
    const [current, setDate] = ctx;
    if (selectFromLink && next.date !== current) {
      setDate(next.date);
    }
  }, [ctx, location, navigationType]);
  return null;
}
